import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";
import { isNotFound, type PinOrderInvoice } from "./pinOrders";

/**
 * Operator wallet (owner 2026-10-15, decision C: pins AND a wallet).
 * Design: operator-wiki common/pricing-model/operator-wallet.md.
 *
 * A buying operator pays its seller in advance; the seller confirms the
 * payment, and only then does the wallet hold it. Every new user or recharge
 * that no pin covers is then priced from the seller's band (price per 30
 * days x days / 30, plus GST) and taken from the wallet -- or refused.
 * radaccess /access/operator/{ns}/wallet*; raduser takes the money.
 *
 * Amounts are integer paise end to end. The browser never prices a grant;
 * it shows the bands and the ledger the server returns.
 */
export interface WalletBand {
  id: number;
  sellernamespace: string;
  name: string;
  maxspeedmbps: number;
  pricepaiseper30d: number;
  gstratebp: number;
  /** The price is what the wallet pays, GST included (owner 2026-10-18). */
  priceincludesgst?: boolean;
  active: boolean;
  updatedat: string;
}

export type WalletEntryKind = "topup" | "deduction" | "refund" | "reversal";
export type WalletEntryStatus = "pending" | "confirmed" | "rejected" | "held" | "released";

export interface WalletEntry {
  id: number;
  holdernamespace: string;
  sellernamespace: string;
  kind: WalletEntryKind;
  status: WalletEntryStatus;
  amountpaise: number;
  netpaise?: number;
  gstpaise?: number;
  bandname?: string;
  itemkey?: string;
  quantity?: number;
  speedmbps?: number;
  termdays?: number;
  groupname?: string;
  paymentreference?: string;
  documentno?: string;
  createdby: string;
  decidedby?: string;
  decidedat?: string;
  decisionreason?: string;
  createdat: string;
}

export interface Wallet {
  sellernamespace: string;
  balancepaise: number;
  pendingpaise: number;
  bands: WalletBand[];
  /** The seller's active item prices (Static IP, extra login, OTT): what an add-on may be linked to. */
  items?: WalletItem[];
  entries: WalletEntry[];
}

/**
 * Pay order (owner 2026-10-18): whether pins or the wallet pay for a plan
 * sale. No choice made = pins first. Static IP / OTT always use the wallet.
 */
export type PayMode = "pins_first" | "wallet_first" | "pins_only" | "wallet_only";

export interface PayModeView {
  paymode: PayMode;
  locked: boolean;
  lockedby?: string;
  lockedat?: string;
}

export const PAY_MODES: { mode: PayMode; label: string; help: string }[] = [
  { mode: "pins_first", label: "Pins first, then funds", help: "A covering pin pays; with none left, the funds pay." },
  { mode: "wallet_first", label: "Funds first, then pins", help: "The funds pay; when they are short, a covering pin pays." },
  { mode: "pins_only", label: "Pins only", help: "Only a pin pays for a plan. No pin left: the sale is refused." },
  { mode: "wallet_only", label: "Funds only", help: "Only the funds pay for a plan. Funds short: the sale is refused." },
];

export const DEFAULT_PAY_MODE: PayModeView = { paymode: "pins_first", locked: false };

export function payModeLabel(m: PayMode): string {
  return PAY_MODES.find((p) => p.mode === m)?.label ?? m;
}

export interface WalletView {
  eligible: boolean;
  wallets: Wallet[];
  paymode?: PayModeView;
}

export interface SellerWallet {
  holdernamespace: string;
  balancepaise: number;
  pendingpaise: number;
  paymode?: PayModeView;
}

export interface SellerWalletView {
  wallets: SellerWallet[];
  entries: WalletEntry[];
}

const op = (ns: string) => `/operator/${encodeURIComponent(ns)}`;

export async function getWallet(identity: CallerIdentity, ns: string, signal?: AbortSignal): Promise<WalletView> {
  const r = await callGateway<WalletView | null>(identity, config().gatewayBase, `${op(ns)}/wallet`, signal ? { signal } : {});
  return r.data ?? { eligible: false, wallets: [] };
}

export async function recordTopup(
  identity: CallerIdentity,
  ns: string,
  input: { seller: string; amountpaise: number; paymentreference: string; idempotencykey: string },
): Promise<WalletEntry> {
  const r = await callGateway<WalletEntry>(identity, config().gatewayBase, `${op(ns)}/wallet/topups`, {
    method: "POST",
    body: { ...input, paymentreference: input.paymentreference.trim() },
  });
  return r.data;
}

export async function listSellerWallets(identity: CallerIdentity, ns: string, signal?: AbortSignal): Promise<SellerWalletView> {
  const r = await callGateway<SellerWalletView | null>(identity, config().gatewayBase, `${op(ns)}/wallets/incoming`, signal ? { signal } : {});
  return r.data ?? { wallets: [], entries: [] };
}

export async function decideTopup(identity: CallerIdentity, ns: string, id: number, confirm: boolean, reason = ""): Promise<WalletEntry> {
  const r = await callGateway<WalletEntry>(identity, config().gatewayBase, `${op(ns)}/wallets/topups/${id}/${confirm ? "confirm" : "reject"}`, {
    method: "PUT",
    body: confirm ? {} : { reason: reason.trim() },
  });
  return r.data;
}

export async function listWalletBands(identity: CallerIdentity, ns: string, signal?: AbortSignal): Promise<WalletBand[]> {
  const r = await callGateway<WalletBand[] | null>(identity, config().gatewayBase, `${op(ns)}/walletbands`, signal ? { signal } : {});
  return r.data ?? [];
}

export async function putWalletBand(
  identity: CallerIdentity,
  ns: string,
  input: { name: string; maxspeedmbps: number; pricepaiseper30d: number; gstratebp: number; priceincludesgst?: boolean },
): Promise<WalletBand> {
  const r = await callGateway<WalletBand>(identity, config().gatewayBase, `${op(ns)}/walletbands`, {
    method: "PUT",
    body: { ...input, name: input.name.trim() },
  });
  return r.data;
}

export async function stopWalletBand(identity: CallerIdentity, ns: string, name: string): Promise<void> {
  await callGateway<unknown>(identity, config().gatewayBase, `${op(ns)}/walletbands/${encodeURIComponent(name)}`, { method: "DELETE" });
}

/**
 * Rupees typed by a person -> integer paise, or the reason it cannot be.
 * Up to two decimals; no grouping commas (they are where 1,00,000 and
 * 100,000 get confused).
 */
export function rupeesToPaise(text: string, min = 1, max = 10_000_000): number | string {
  const t = text.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return "Enter rupees as digits, up to 2 decimals, without commas";
  const [whole, frac = ""] = t.split(".");
  const paise = Number(whole) * 100 + Number(frac.padEnd(2, "0"));
  if (!Number.isSafeInteger(paise)) return "That amount is too large";
  if (paise < min * 100 || paise > max * 100) return `Enter between ₹${min.toLocaleString("en-IN")} and ₹${max.toLocaleString("en-IN")}`;
  return paise;
}

/** GST basis points (1800) from a percent typed by a person ("18"). */
export function gstPercentToBP(text: string): number | string {
  const t = text.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return "Enter GST as a percent, e.g. 18";
  const bp = Math.round(Number(t) * 100);
  if (bp < 0 || bp > 2800) return "GST must be 0% to 28%";
  return bp;
}

/** What a 30-day grant on this band costs including GST, in paise -- for display only; the server prices every grant. */
export function bandGross30d(b: Pick<WalletBand, "pricepaiseper30d" | "gstratebp" | "priceincludesgst">): number {
  if (b.priceincludesgst) return b.pricepaiseper30d;
  const gst = Math.floor((b.pricepaiseper30d * b.gstratebp * 2 + 10000) / 20000);
  return b.pricepaiseper30d + gst;
}

/**
 * The net and GST inside a GST-inclusive 30-day price, worked backwards
 * exactly as raduser priceGrantInclusive does (half-up; net + GST = price).
 */
export function inclusiveSplit30d(pricepaise: number, gstratebp: number): { net: number; gst: number } {
  const den = 10000 + gstratebp;
  const net = Math.floor((pricepaise * 10000 * 2 + den) / (2 * den));
  return { net, gst: pricepaise - net };
}

/** The holder choosing its own pay order. 409 while its seller has locked it. */
export async function setPayMode(identity: CallerIdentity, ns: string, paymode: PayMode): Promise<PayModeView> {
  const r = await callGateway<PayModeView>(identity, config().gatewayBase, `${op(ns)}/wallet/paymode`, { method: "PUT", body: { paymode } });
  return r.data;
}

/** The seller setting a buyer's pay order, and locking it (or giving the choice back). */
export async function setBuyerPayMode(identity: CallerIdentity, ns: string, holder: string, paymode: PayMode, locked: boolean): Promise<PayModeView> {
  const r = await callGateway<PayModeView>(identity, config().gatewayBase, `${op(ns)}/wallets/${encodeURIComponent(holder)}/paymode`, {
    method: "PUT",
    body: { paymode, locked },
  });
  return r.data;
}

export const WALLET_KIND_LABEL: Record<WalletEntryKind, string> = {
  topup: "Funds added",
  deduction: "Activation",
  refund: "Refund",
  reversal: "Reversal",
};

export const WALLET_STATUS_LABEL: Record<WalletEntryStatus, string> = {
  pending: "Waiting for seller",
  confirmed: "Done",
  rejected: "Rejected",
  held: "Held for a sale",
  released: "Released",
};

/** Signed amount for the ledger: money in is +, money out is -. */
export function signedPaise(e: Pick<WalletEntry, "kind" | "amountpaise">): number {
  return e.kind === "topup" || e.kind === "reversal" ? e.amountpaise : -e.amountpaise;
}

/**
 * The seller's tax invoice for one CONFIRMED top-up (owner 2026-10-15: "when
 * operator buy credit balance in wallet then upstream provider issue an
 * invoice for it"). It totals exactly the money that arrived, GST cut out of
 * it. Activations are not invoiced again. Same document shape as a pin
 * order's invoice; radbilling holds it.
 */
const topupInvoicePath = (ns: string, id: number) => `${op(ns)}/wallets/topups/${id}/invoice`;

export async function getTopupInvoice(identity: CallerIdentity, ns: string, id: number, signal?: AbortSignal): Promise<PinOrderInvoice | null> {
  try {
    const r = await callGateway<PinOrderInvoice>(identity, config().gatewayBase, topupInvoicePath(ns, id), signal ? { signal } : {});
    return r.data ?? null;
  } catch (e) {
    if (isNotFound(e)) return null; // the normal answer until it is raised
    throw e;
  }
}

export async function raiseTopupInvoice(identity: CallerIdentity, ns: string, id: number): Promise<PinOrderInvoice> {
  const r = await callGateway<PinOrderInvoice>(identity, config().gatewayBase, topupInvoicePath(ns, id), { method: "POST" });
  return r.data;
}

/** Only money the seller accepted is a supply to invoice. */
export function canInvoiceTopup(e: Pick<WalletEntry, "kind" | "status">): boolean {
  return e.kind === "topup" && e.status === "confirmed";
}

/**
 * Upstream items (owner 2026-10-15): the SELLER prices every item it supplies
 * beyond plans -- a Static public IP, an OTT pack. A buyer's wallet pays for
 * them (pins cover plans only). The buyer's own component only names the
 * item (labels.upstream_item); this list is what the seller charges.
 * Recurring items are priced per 30 days; one-time items are flat.
 */
export type WalletItemBilling = "recurring" | "one_time";

export interface WalletItem {
  id: number;
  sellernamespace: string;
  itemkey: string;
  name: string;
  billing: WalletItemBilling;
  pricepaise: number;
  gstratebp: number;
  /** The price is what the wallet pays, GST inside it (radaccess v1.122+; absent before = false). */
  priceincludesgst?: boolean;
  active: boolean;
}

/** What one item costs the wallet for 30 days (recurring) or once, GST included -- display only; radaccess prices every hold. */
export function itemGross(it: Pick<WalletItem, "pricepaise" | "gstratebp" | "priceincludesgst">): number {
  return bandGross30d({ pricepaiseper30d: it.pricepaise, gstratebp: it.gstratebp, priceincludesgst: Boolean(it.priceincludesgst) });
}

export async function listWalletItems(identity: CallerIdentity, ns: string, signal?: AbortSignal): Promise<WalletItem[]> {
  const r = await callGateway<WalletItem[] | null>(identity, config().gatewayBase, `${op(ns)}/walletitems`, signal ? { signal } : {});
  return r.data ?? [];
}

export async function putWalletItem(
  identity: CallerIdentity,
  ns: string,
  input: { itemkey: string; name: string; billing: WalletItemBilling; pricepaise: number; gstratebp: number; priceincludesgst: boolean },
): Promise<WalletItem> {
  const r = await callGateway<WalletItem>(identity, config().gatewayBase, `${op(ns)}/walletitems`, {
    method: "PUT",
    body: { ...input, itemkey: input.itemkey.trim(), name: input.name.trim() },
  });
  // A server older than radaccess v1.122 drops the field and stores the price as + GST: say so, never show it as saved.
  if (input.priceincludesgst && input.gstratebp > 0 && !r.data?.priceincludesgst) {
    throw new Error("The server saved this price as GST added on top, not included: it does not support included GST yet. Change the price back, and tell Ankh.");
  }
  return r.data;
}

export async function stopWalletItem(identity: CallerIdentity, ns: string, itemkey: string): Promise<void> {
  await callGateway<unknown>(identity, config().gatewayBase, `${op(ns)}/walletitems/${encodeURIComponent(itemkey)}`, { method: "DELETE" });
}

/** The item keys the price file and the plan builder use (4d, v1). */
export const UPSTREAM_ITEM_SUGGESTIONS: { key: string; name: string; billing: WalletItemBilling }[] = [
  { key: "static_ip", name: "Static public IP", billing: "recurring" },
  { key: "static_ip_activation", name: "Static IP activation", billing: "one_time" },
  { key: "extra_login", name: "Extra login (one more device at a time)", billing: "recurring" },
];

/** The suggestions a seller has not priced yet: a key already in its list (stopped too) is changed or restarted there, not added again. */
export function unpricedSuggestions(priced: readonly { itemkey: string }[]): typeof UPSTREAM_ITEM_SUGGESTIONS {
  const have = new Set(priced.map((it) => it.itemkey));
  return UPSTREAM_ITEM_SUGGESTIONS.filter((s) => !have.has(s.key));
}

const words = (s: string) => s.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);

/**
 * The one upstream item this add-on is (owner 2026-10-18: no dropdown -- show
 * the match, Link or Unlink). An item matches when every word of its key is in
 * the add-on's key or name ("static_ip" is in "Static public IP for home
 * plans") and both are charged the same way (once / recurring). Of several, the
 * one with the most words wins ("static_ip_activation" over "static_ip" for
 * "Static IP activation"); a tie is no match -- never a guess.
 */
export function matchUpstreamItem(
  c: { name: string; billingtype: string; labels?: { addon?: string } | null },
  items: readonly WalletItem[],
): WalletItem | null {
  const have = new Set([...words(c.labels?.addon ?? ""), ...words(c.name)]);
  const billing: WalletItemBilling | null = c.billingtype === "ONE_TIME" ? "one_time" : c.billingtype === "RECURRING" ? "recurring" : null;
  let best: WalletItem | null = null;
  let bestN = 0;
  let tie = false;
  for (const it of items) {
    if (it.billing !== billing) continue;
    const need = words(it.itemkey);
    if (need.length === 0 || !need.every((w) => have.has(w))) continue;
    if (need.length > bestN) {
      best = it;
      bestN = need.length;
      tie = false;
    } else if (need.length === bestN) tie = true;
  }
  return tie ? null : best;
}

/** Every item an operator's sellers price, once each, for the "Supplied by your upstream" picker. */
export function upstreamItemsOf(view: Pick<WalletView, "wallets"> | null | undefined): WalletItem[] {
  const seen = new Map<string, WalletItem>();
  for (const w of view?.wallets ?? []) for (const it of w.items ?? []) if (!seen.has(it.itemkey)) seen.set(it.itemkey, it);
  return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/** Same rule as radaccess itemKeyRE: lower-case, digits, _ ; up to three parts joined by ":". */
export function itemKeyError(key: string): string | null {
  return /^[a-z0-9_]{2,32}(:[a-z0-9_]{1,32}){0,2}$/.test(key.trim()) ? null : "Lower-case letters, digits and _ ; up to three parts joined by ':' (e.g. static_ip, ott:partner:premium_30)";
}

/** A ledger time: India time (radaccess v1.132+ sends real instants). */
export { istTime as ledgerTime } from "../domain/istTime";
